import toast from 'react-hot-toast';
import { generateSalePDF } from './salePdfGenerator';

// ─── Logo en Base64 ────────────────────────────────────────────────────────────
let LOGO_BASE64: string | null = null;

async function loadLogoBase64(): Promise<string | null> {
  if (LOGO_BASE64) return LOGO_BASE64;
  try {
    const mod = await import('../assets/Maria_Regna_Logotipo.png');
    const url: string = mod.default;
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        LOGO_BASE64 = reader.result as string;
        resolve(LOGO_BASE64);
      };
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── Ticket Impreso (Ventana del navegador) ────────────────────────────────────
export const printTicketWindow = async (sale: any) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const total = Number(sale.total || 0);
  const amountPaid = Number(sale.amountPaid || 0);
  const totalPaidCalc = sale.payments?.reduce((sum: number, p: any) => sum + Number(p.amount), 0) || amountPaid;
  const remaining = Math.max(0, total - totalPaidCalc);

  // Referencia a la venta original (para abonos/liquidaciones)
  const parentFolio = sale.parentSale?.folio || (sale.parentSaleId ? `#${sale.parentSaleId}` : null);
  const ctx = sale.saleContext;
  const globalTotal = ctx ? Number(ctx.totalGlobal) : null;
  const globalPaid = ctx ? Number(ctx.totalPaidGlobal) : null;
  const globalBalance = ctx ? (ctx.balance !== undefined ? Number(ctx.balance) : null) : null;

  // Detección de tipo de nota por saleType (más confiable que el status)
  const isAbono = sale.saleType === 'ABONO' || !!sale.parentSaleId;
  
  // Para un abono, es liquidación SOLO si el saldo GLOBAL es cero
  const isLiquidacion = sale.saleType === 'LIQUIDACION' || (isAbono && globalBalance !== null && globalBalance < 0.01);
  
  const isLayaway = !isAbono && (
    sale.status === 'APARTADO' || sale.status === 'PENDIENTE' || sale.status === 'PRE-ORDER' ||
    (totalPaidCalc < total && totalPaidCalc > 0)
  );
  const isLiquidation = !isAbono && sale.status === 'COMPLETADA' && totalPaidCalc >= total - 0.01 && (sale.payments?.length || 0) > 1;
  const hasPreOrderItems = sale.items?.some((i: any) => i.isPreOrder) || sale.status === 'PRE-ORDER';
  const showPaymentHistory = isAbono || isLayaway || isLiquidation || isLiquidacion;

  const receiptType = isLiquidacion
    ? 'LIQUIDACION'
    : isAbono
      ? 'ABONO'
      : isLiquidation
        ? 'LIQUIDACION'
        : (isLayaway ? 'ABONO' : 'VENTA');


  const logoBase64 = await loadLogoBase64();

  // Si cargó el logo, solo muestra la imagen. Si no, muestra el nombre en serif.
  const logoHtml = logoBase64
    ? `<img src="${logoBase64}" alt="Maria Regna" style="max-width:180px; max-height:100px; object-fit:contain;" />`
    : `<div style="font-family:'Georgia',serif; font-size:19px; font-weight:700; letter-spacing:3px; text-transform:uppercase;">MARIA REGNA</div>`;

  const itemsHtml = (sale.items || []).map((item: any) => {
    const totalInc = Number(item.total || 0);
    const p = item.product || {};
    const color = p.color || '-';
    const talla = p.talla || '-';
    const codigoProveedor = p.codigo_proveedor || p.marca || 'GENERICO';
    const codigo = p.cod_producto || '';
    return `
      <div class="item-row">
        <div class="item-main">
          <span class="item-brand">${item.quantity} x ${codigoProveedor}</span>
          <span class="item-price">$${totalInc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
        <div class="item-details">
          Mod: <strong>${codigo}</strong> | Talla: <strong>${talla}</strong> | Color: <strong>${color}</strong>
        </div>
        ${item.isPreOrder ? '<div class="preorder-badge">* PEDIDO ESPECIAL</div>' : ''}
      </div>`;
  }).join('');

  // paymentsHtml usa showPaymentHistory definido arriba
  const paymentsHtml = (sale.payments || []).length > 0
    ? (sale.payments || []).map((p: any) => `
        <div class="payment-row">
          <span>${new Date(p.createdAt).toLocaleDateString('es-MX')} - ${p.method}</span>
          <span>$${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>`).join('')
    : (amountPaid > 0 ? `
        <div class="payment-row">
          <span>${new Date(sale.createdAt).toLocaleDateString('es-MX')} - ${sale.paymentMethod || 'Pago'}</span>
          <span>$${amountPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>` : '');

  const saleDate = new Date(sale.createdAt);
  const formattedDate = saleDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
  const formattedTime = saleDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Ticket #${sale.folio || sale.id}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      font-size: 12px;
      width: 80mm;
      padding: 0 5px 20px;
      color: #000;
      background: #fff;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* HEADER */
    .header {
      text-align: center;
      padding: 10px 0 8px;
      border-bottom: 2px solid #000;
    }
    .tagline {
      font-size: 8px;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: #000;
      margin-top: 3px;
    }
    .badge {
      display: inline-block;
      background: #000;
      color: #fff;
      font-size: 8px;
      letter-spacing: 1px;
      text-transform: uppercase;
      padding: 2px 8px;
      margin-top: 5px;
    }

    /* FOLIO */
    .folio-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 6px 0 5px;
      border-bottom: 1px dashed #000;
    }
    .folio-label { font-size: 7px; text-transform: uppercase; letter-spacing: 1.5px; color: #000; }
    .folio-number { font-size: 13px; font-weight: 700; }
    .folio-date { text-align: right; font-size: 9px; color: #000; line-height: 1.5; }

    /* CLIENTE */
    .section-label {
      font-size: 7px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #000;
      margin-bottom: 2px;
      font-weight: 700;
    }
    .client-section {
      padding: 6px 0;
      border-bottom: 1px dashed #000;
    }
    .client-name { font-size: 12px; font-weight: 700; color: #000; }
    .client-phone { font-size: 10px; color: #000; margin-top: 1px; }
    .event-row {
      margin-top: 4px;
      font-size: 10px;
      font-weight: 700;
      color: #000;
      display: flex;
      align-items: center;
      gap: 5px;
    }
    .event-tag {
      background: #000;
      color: #fff;
      font-size: 7px;
      letter-spacing: 1px;
      padding: 1px 5px;
      text-transform: uppercase;
    }

    /* ITEMS */
    .items-section { padding-top: 4px; }
    .item-row {
      padding: 5px 0;
      border-bottom: 1px dotted #000;
    }
    .item-main {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      margin-bottom: 2px;
    }
    .item-brand { font-size: 12px; font-weight: 700; color: #000; }
    .item-price { font-size: 12px; font-weight: 700; color: #000; }
    .item-details { font-size: 9px; color: #000; line-height: 1.4; }
    .preorder-badge { font-size: 8px; color: #000; font-style: italic; margin-top: 2px; }

    /* TOTALES */
    .totals-section {
      padding: 7px 0 5px;
      border-top: 1px solid #000;
      border-bottom: 1px solid #000;
      margin-top: 3px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      margin-bottom: 2px;
      color: #000;
    }
    .total-row.main { font-size: 14px; font-weight: 900; margin-bottom: 3px; }
    .total-row.restante { font-size: 13px; font-weight: 900; }
    .total-row.small { font-size: 9px; font-style: italic; }
    .payment-method { font-size: 9px; color: #000; margin-top: 3px; }

    /* ABONOS */
    .payments-section { padding: 4px 0; border-bottom: 1px dashed #000; }
    .payment-row {
      display: flex;
      justify-content: space-between;
      font-size: 9px;
      margin-bottom: 2px;
      color: #000;
    }

    /* FOOTER */
    .footer { text-align: center; padding-top: 10px; }
    .footer-thanks { font-size: 10px; font-style: italic; color: #000; margin-bottom: 7px; }
    .policy-box {
      border: 1.5px solid #000;
      padding: 5px 6px;
      margin: 5px 0;
      text-align: center;
    }
    .policy-title { font-size: 9px; font-weight: 900; letter-spacing: 1px; text-transform: uppercase; color: #000; }
    .policy-sub { font-size: 8px; color: #000; margin-top: 1px; }
    .preorder-notice {
      font-size: 9px;
      font-weight: 700;
      color: #000;
      margin: 5px 0;
      padding: 4px;
      border: 1px dashed #000;
    }
    .whatsapp-section { margin-top: 7px; font-size: 9px; font-weight: 700; color: #000; line-height: 1.6; }
    .whatsapp-number { font-size: 12px; font-weight: 900; }
    .ornament { font-size: 10px; color: #000; letter-spacing: 4px; margin-top: 8px; }
  </style>
</head>
<body>

  <!-- HEADER -->
  <div class="header">
    ${logoHtml}
    <div class="tagline">M O D A   &amp;   A L T A   C O S T U R A</div>
    <div class="badge">${receiptType === 'LIQUIDACION' ? 'COMPROBANTE DE LIQUIDACION' : receiptType === 'ABONO' ? 'COMPROBANTE DE ABONO' : 'COMPROBANTE DE VENTA'}</div>
    ${hasPreOrderItems ? '<div class="badge" style="margin-top:2px;">Pedido Especial</div>' : ''}
    ${sale.notes && sale.notes.includes('MAYOREO') ? '<div class="badge" style="margin-top:2px;">Compra por Mayoreo</div>' : ''}
  </div>

  <!-- FOLIO -->
  <div class="folio-row">
    <div>
      <div class="folio-label">Folio</div>
      <div class="folio-number">#${sale.folio || sale.id}</div>
    </div>
    <div class="folio-date">${formattedDate}<br/>${formattedTime}</div>
  </div>

  <!-- CLIENTE -->
  <div class="client-section">
    <div class="section-label">Cliente</div>
    <div class="client-name">${sale.customerName || 'Publico General'}</div>
    <div class="client-phone">Tel: ${sale.customerPhone || 'N/A'}</div>
    ${sale.eventDate ? `
      <div class="event-row">
        <span class="event-tag">Fecha Evento</span>
        <strong>${new Date(sale.eventDate).toLocaleDateString('es-MX', { timeZone: 'UTC', day: '2-digit', month: 'long', year: 'numeric' })}</strong>
      </div>` : ''}
  </div>

  <!-- REFERENCIA DE VENTA ORIGINAL (solo para abonos/liquidaciones) -->
  ${isAbono && parentFolio ? `
  <div style="background:#f5f5f5; border:1.5px solid #000; padding:7px 6px; margin:5px 0; border-radius:0;">
    <div class="section-label" style="margin-bottom:3px;">Referencia de Venta</div>
    <div style="font-size:13px; font-weight:900;">Folio original: ${parentFolio}</div>
    ${globalTotal !== null ? `
    <div style="margin-top:4px; border-top:1px dashed #999; padding-top:4px;">
      <div style="display:flex; justify-content:space-between; font-size:9px; color:#000; margin-bottom:1px;">
        <span>Total de la venta</span>
        <span>$${globalTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      <div style="display:flex; justify-content:space-between; font-size:9px; color:#000; margin-bottom:1px;">
        <span>Total abonado</span>
        <span>$${(globalPaid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      <div style="display:flex; justify-content:space-between; font-size:11px; font-weight:900; color:#000; margin-top:2px;">
        <span>${(globalBalance || 0) < 0.01 ? 'SALDO: LIQUIDADO' : 'Saldo pendiente'}</span>
        <span>${(globalBalance || 0) < 0.01 ? 'PAGADO' : '$' + (globalBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
    </div>` : ''}
  </div>` : ''}

  <!-- PRENDAS (solo si hay items) -->
  ${(sale.items || []).length > 0 ? `
  <div class="items-section">
    <div class="section-label" style="padding-top:5px;">Prendas</div>
    ${itemsHtml}
  </div>` : ''}

  <!-- TOTALES -->
  <div class="totals-section">
    ${Number(sale.discount || 0) > 0 ? `
      <div class="total-row small">
        <span>Descuento</span><span>-$${Number(sale.discount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>` : ''}
    ${Number(sale.shippingCost || 0) > 0 ? `
      <div class="total-row small">
        <span>Envio</span><span>+$${Number(sale.shippingCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>` : ''}
    ${Number(sale.alterationCost || 0) > 0 ? `
      <div class="total-row small">
        <span>Arreglos</span><span>+$${Number(sale.alterationCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>` : ''}
    <div class="total-row main">
      <span>${isAbono ? 'ESTE ABONO' : 'TOTAL'}</span>
      <span>$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </div>
    ${isAbono ? `
      <div class="payment-method">Metodo de pago: <strong>${sale.paymentMethod}</strong></div>
      ${isLiquidacion ? '<div class="total-row" style="font-weight:900; margin-top:4px;"><span>LIQUIDACION COMPLETA</span><span>PAGADO</span></div>' : ''}` :
      (isLayaway || isLiquidation) ? `
      <div class="total-row">
        <span>Total Abonado</span>
        <span>$${totalPaidCalc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>
      ${remaining > 0.01 ? `
      <div class="total-row restante">
        <span>SALDO RESTANTE</span>
        <span>$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
      </div>` : `
      <div class="total-row" style="color:#000; font-weight:900;">
        <span>LIQUIDADO</span><span>PAGADO</span>
      </div>`}` : `
      <div class="payment-method">Metodo de pago: <strong>${sale.paymentMethod}</strong></div>`}
  </div>

  <!-- HISTORIAL DE ABONOS -->
  ${showPaymentHistory && paymentsHtml ? `
    <div class="payments-section">
      <div class="section-label" style="padding-top:4px;">Historial de Abonos</div>
      ${paymentsHtml}
    </div>` : ''}

  <!-- FOOTER -->
  <div class="footer">
    <div class="footer-thanks">¡Gracias por su preferencia!</div>

    <div class="policy-box">
      <div class="policy-title">NO CAMBIOS NI DEVOLUCIONES</div>
      <div class="policy-sub">Politica oficial de la boutique</div>
    </div>

    <div class="policy-box">
      <div class="policy-title">Apartado a 30 dias</div>
      <div class="policy-sub">Pasado este plazo se pierde el anticipo</div>
    </div>

    ${hasPreOrderItems ? `
      <div class="preorder-notice">
        Contacto Modistas:<br/>CARMEN +52 449 306 0184<br/>PILAR +52 449 358 9479
      </div>` : ''}

    <div class="whatsapp-section">
      WhatsApp de atencion<br/>
      <span class="whatsapp-number">449 436 2610</span>
    </div>

    <div class="ornament">* * *</div>
  </div>

  <script>
    window.onload = function() {
      setTimeout(function() { window.print(); }, 450);
    };
  </script>
</body>
</html>`;

  printWindow.document.write(html);
  printWindow.document.close();
};

// ─── Envío por WhatsApp ────────────────────────────────────────────────────────
// Usa solo texto ASCII para evitar caracteres ? en dispositivos Android.
export const sendTicketWhatsApp = async (sale: any) => {
  if (!sale.customerPhone) {
    toast.error('El cliente no tiene telefono registrado');
    return;
  }

  try {
    await generateSalePDF(sale);

    const cleanPhone = sale.customerPhone.replace(/[+\s-]/g, '');

    await new Promise(resolve => setTimeout(resolve, 1000));

    const customerName = sale.customerName && sale.customerName !== 'General'
      ? sale.customerName.split(' ')[0]
      : '';

    const totalAbono = Number(sale.total || 0);
    const ctx = sale.saleContext;
    const globalBalance = ctx ? Number(ctx.balance) : null;
    const parentFolio = sale.parentSale?.folio || (sale.parentSaleId ? `#${sale.parentSaleId}` : null);

    const isAbonoNote = sale.saleType === 'ABONO' || !!sale.parentSaleId;
    const isLiquidacionNote = sale.saleType === 'LIQUIDACION' || (isAbonoNote && globalBalance !== null && globalBalance < 0.01);

    // Formato amigable, sin emojis para compatibilidad universal
    let message = 
      `*BOUTIQUE MARIA REGNA*\n` +
      `_Moda & Alta Costura_\n\n` +
      `Hola${customerName ? ' *' + customerName + '*' : ''}!\n`;

    if (isLiquidacionNote) {
      message += `Te compartimos el recibo de *LIQUIDACION COMPLETA* de tu compra (Folio original: ${parentFolio}).\n\n`;
    } else if (isAbonoNote) {
      message += `Te compartimos tu recibo de *ABONO* (Vinculado a la venta: ${parentFolio}).\n\n`;
    } else {
      message += `Gracias por tu compra en *MARIA REGNA*. Te compartimos tu resumen:\n\n`;
    }

    message += `*Folio:* #${sale.folio || sale.id}\n`;

    if (isAbonoNote) {
      message += `*Monto de este abono:* $${totalAbono.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
      if (globalBalance !== null) {
        message += `*Saldo pendiente global:* $${globalBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
      }
    } else {
      message += `*Total:* $${totalAbono.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
      const amountPaid = sale.payments?.reduce((s: number, p: any) => s + Number(p.amount), 0) || Number(sale.amountPaid || 0);
      const remaining = Math.max(0, totalAbono - amountPaid);
      if (remaining > 0.01) {
        message += `*Abonado:* $${amountPaid.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
        message += `*Saldo restante:* $${remaining.toLocaleString('en-US', { minimumFractionDigits: 2 })}\n`;
      } else {
        message += `*Pago completado*\n`;
      }
    }

    message +=
      `\n_No cambios ni devoluciones._\n` +
      (isAbonoNote && globalBalance && globalBalance > 0.01 
        ? `_Recuerda que tu apartado tiene una vigencia de 30 dias._`
        : `_Gracias por tu preferencia._`);

    toast.success('PDF descargado. Abriendo WhatsApp...');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  } catch (error) {
    console.error('Error al enviar por WhatsApp:', error);
    toast.error('Error al generar el recibo. Por favor intenta nuevamente.');
  }
};
